import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLE_SUPER_ADMIN, ROLE_ORG_ADMIN, ROLE_ORG_USER } from '../contexts/AuthContext';
import { adminAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function AdminUserFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user, isSuperAdmin, isAdmin, organizationId, organizationName } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [organizations, setOrganizations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ROLE_ORG_USER,
    organization_id: '',
    is_active: true
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    if (isSuperAdmin) {
      fetchOrganizations();
    } else {
      // Org admins auto-set their organization
      setFormData(prev => ({ ...prev, organization_id: organizationId }));
    }
    
    if (isEdit) {
      fetchUser();
    }
  }, [isAdmin, isEdit, id, navigate, isSuperAdmin, organizationId]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to load organizations');
    }
  };

  const fetchUser = async () => {
    try {
      const response = await adminAPI.getUser(id);
      setFormData({
        name: response.data.name,
        email: response.data.email,
        password: '',
        role: response.data.role,
        organization_id: response.data.organization_id || '',
        is_active: response.data.is_active
      });
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!isEdit && !formData.password.trim()) {
      toast.error('Password is required');
      return;
    }
    if (!isEdit && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Organization is required for non-super-admin users
    if (formData.role !== ROLE_SUPER_ADMIN && !formData.organization_id) {
      toast.error('Organization is required for non-super-admin users');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        organization_id: formData.role === ROLE_SUPER_ADMIN ? null : formData.organization_id,
        is_active: formData.is_active
      };
      
      if (formData.password) {
        submitData.password = formData.password;
      }
      
      if (isEdit) {
        await adminAPI.updateUser(id, submitData);
        toast.success('User updated successfully');
      } else {
        await adminAPI.createUser(submitData);
        toast.success('User created successfully');
      }
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin/users" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors"
            data-testid="back-to-users"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Users
          </Link>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
            {isEdit ? 'Edit User' : 'Add New User'}
          </h1>
          <p className="font-body text-muted-foreground">
            {isEdit ? 'Update user account details' : 'Create a new user account'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-xl" data-testid="user-form">
          <div className="bg-card rounded-xl border border-border p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter full name"
                className="font-body bg-card"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-body">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="user@example.com"
                className="font-body bg-card"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-body">
                Password {isEdit ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={isEdit ? '••••••••' : 'Enter password'}
                className="font-body bg-card"
                required={!isEdit}
                data-testid="password-input"
              />
              {!isEdit && (
                <p className="text-xs text-muted-foreground font-body">
                  Must be at least 6 characters
                </p>
              )}
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label className="font-body">User Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => handleChange('role', v)}
                disabled={!isSuperAdmin && formData.role === ROLE_SUPER_ADMIN}
              >
                <SelectTrigger className="font-body bg-card" data-testid="role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && (
                    <SelectItem value={ROLE_SUPER_ADMIN}>Super Admin (Full Access)</SelectItem>
                  )}
                  <SelectItem value={ROLE_ORG_ADMIN}>Organization Admin</SelectItem>
                  <SelectItem value={ROLE_ORG_USER}>Organization User</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground font-body">
                {formData.role === ROLE_SUPER_ADMIN && 'Super admins have access to all organizations and settings'}
                {formData.role === ROLE_ORG_ADMIN && 'Org admins can manage users and cases within their organization'}
                {formData.role === ROLE_ORG_USER && 'Users can view and manage cases within their organization'}
              </p>
            </div>

            {/* Organization Selection (for non-super-admin roles) */}
            {formData.role !== ROLE_SUPER_ADMIN && (
              <div className="space-y-2">
                <Label className="font-body">Organization *</Label>
                {isSuperAdmin ? (
                  <Select 
                    value={formData.organization_id} 
                    onValueChange={(v) => handleChange('organization_id', v)}
                  >
                    <SelectTrigger className="font-body bg-card" data-testid="org-select">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={organizationName || ''}
                    disabled
                    className="font-body bg-secondary"
                  />
                )}
              </div>
            )}

            {isEdit && (
              <div className="flex items-center justify-between py-4 border-t border-b border-border">
                <div>
                  <Label className="font-body font-medium">Account Active</Label>
                  <p className="font-body text-sm text-muted-foreground">
                    Disabled accounts cannot log in
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                  disabled={id === user?.id}
                  data-testid="is-active-switch"
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4">
              <Link to="/admin/users">
                <Button variant="outline" className="font-body" data-testid="cancel-btn">
                  Cancel
                </Button>
              </Link>
              <Button 
                type="submit" 
                className="btn-lift bg-primary text-white font-body"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEdit ? 'Update User' : 'Create User'
                )}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
